namespace Solovis.Reports.ReportHelpers.ManagerList {

  // The bulk-loaded "parentManagerValues" data set ManagerList.js's report exposes.
  export interface ParentManagerDataSet {
    propertyDefinition?: InvData.PropertyDefinition;
    index: Solovis.Reports.DataSetHelpers.ResourcePropertyValueIndex;
    childrenByParentResourceId: Record<number, InvData.Models.JsonManager[]>;
  }

  // Fields this file's cell formatters read off a DataTableControl htmlFormatter's cellContext.
  // A manager with its own children renders via the platform's group-header row path, which
  // hands us the tree wrapper (_backingRow holds the real manager) rather than the row itself.
  export interface ManagerCellFormatterContext {
    aggregate?: boolean;
    group?: boolean;
    defaultFormatter: (cellValue: any, cellContext: any) => any;
    dataTable: Solovis.Reports.Parts.Tables.DataTableControl;
    row: InvData.Models.JsonManager & { _backingRow?: InvData.Models.JsonManager };
  }

  // Bulk-loaded Sys.ParentManager value for a manager row, or null if unset / not yet loaded.
  export function getParentManagerPropValueInstance(
    tableControl: Solovis.Reports.Parts.Tables.DataTableControl,
    row: InvData.Models.JsonManager
  ): Solovis.Reports.DataSetHelpers.ResourcePropertyValueInstance | null {
    const parentManagerDataSet: ParentManagerDataSet = tableControl.dataSets.parentManagerValues;
    if (!parentManagerDataSet || !parentManagerDataSet.propertyDefinition) {
      return null;
    }

    return parentManagerDataSet.index.getPropValue(row.resourceId, parentManagerDataSet.propertyDefinition.id);
  }

  export function getParentManagerDisplayName(
    tableControl: Solovis.Reports.Parts.Tables.DataTableControl,
    row: InvData.Models.JsonManager
  ): string | null {
    const propValueInstance = getParentManagerPropValueInstance(tableControl, row);
    return propValueInstance && propValueInstance.valueStrings && propValueInstance.valueStrings.length ? propValueInstance.valueStrings[0] : null;
  }

  // Renders the "Parent Manager" column: the immediate parent as a link, or blank.
  export function parentManagerColumnFormatter(cellValue: any, cellContext: ManagerCellFormatterContext): any {
    // Grouped by this column: the row is the group wrapper (no manager to look up), and cellValue
    // is already the group's value - the parent's name from this column's field.
    if (cellContext.group) {
      return cellContext.defaultFormatter(cellValue, cellContext);
    }

    const row = cellContext.row._backingRow || cellContext.row;

    const propValueInstance = getParentManagerPropValueInstance(cellContext.dataTable, row);
    if (!propValueInstance) {
      return "";
    }

    return Solovis.Reports.Formatter.formatResourcePropertyValueAsHtml({
      property: cellContext.dataTable.dataSets.parentManagerValues.propertyDefinition,
      resourcePropertyValue: propValueInstance
    });
  }

  // Replicates FilterManager's per-row check so tree building can test any candidate manager.
  export function doesManagerRowMatchActiveFilter(
    tableControl: Solovis.Reports.Parts.Tables.DataTableControl,
    row: InvData.Models.JsonManager
  ): boolean {
    const filterManager = tableControl.filterManager;
    if (!filterManager.isFilteringEnabled()) {
      return true;
    }

    const columnSet = tableControl.columnSet;
    const sortManager = tableControl.sortManager;
    let matches = true;

    for (const filterByEntry of filterManager._filterBy) {
      const col = columnSet.getColumnById(filterByEntry.id);
      if (!col) {
        continue;
      }

      const sort = sortManager.getSortById(filterByEntry.id);
      const sortValue = sort ? sort.getter(row) : undefined;
      const cellValue = tableControl._getColumnValueForRow(col, row);

      if (!filterManager._doesValueMatch(filterByEntry, col, sort, sortValue, cellValue)) {
        matches = false;
      }
    }

    return matches;
  }

  // Builds a resourceId -> direct-children index from the full manager list, for subRowFactory.
  export function buildManagerChildrenIndex(
    managers: InvData.Models.JsonManager[],
    parentManagerValueIndex: Solovis.Reports.DataSetHelpers.ResourcePropertyValueIndex,
    parentManagerPropDefId: number
  ): Record<number, InvData.Models.JsonManager[]> {
    const childrenByParentResourceId: Record<number, InvData.Models.JsonManager[]> = {};

    for (const manager of managers) {
      const value = parentManagerValueIndex.getValue(manager.resourceId, parentManagerPropDefId);
      const parentResourceId = value && value.r && value.r.length ? value.r[0] : null;

      if (parentResourceId) {
        if (!childrenByParentResourceId[parentResourceId]) {
          childrenByParentResourceId[parentResourceId] = [];
        }
        childrenByParentResourceId[parentResourceId].push(manager);
      }
    }

    return childrenByParentResourceId;
  }
}
